<template>
  <div class="calificar-opinion">
    <div class="header-flex">
      <h1>Calificar instrumento de opinión</h1>
      <div class="header-acciones">
        <button v-if="rubrica" class="btn salir" @click="verRubrica = true">Ver rúbrica</button>
        <button class="btn salir" @click="$router.back()">Salir</button>
      </div>
    </div>
    <p class="subtitulo">
      Cada pregunta califica su propio aspecto. La nota de un indicador es el promedio de sus preguntas.
    </p>
    <p v-if="nombreVersion" class="ctx-modelo">{{ nombreVersion }}</p>

    <div class="divider"></div>

    <p v-if="cargando" class="info">Cargando…</p>
    <p v-else-if="error" class="error">{{ error }}</p>
    <p v-else-if="esFormularioCompleto" class="info">
      Este formulario califica como una sola unidad: su nota se registra desde la ficha de
      opinión, sobre el formulario completo, no pregunta por pregunta.
    </p>
    <p v-else-if="!aspectos.length" class="info">
      Este instrumento no tiene preguntas con indicador asignado.
    </p>

    <template v-else>
      <!-- Rúbrica de opinión, siempre a mano mientras se califica -->
      <details v-if="rubrica" class="rubrica-inline" open>
        <summary>Rúbrica de opinión — tabla de valoración</summary>
        <RubricaTablaOpinion :rubrica="rubrica.criterio" />
      </details>

      <div v-for="aspecto in aspectos" :key="aspecto.id_componente ?? aspecto.aspecto" class="aspecto">
        <div v-if="aspecto.id_componente" class="ctx-padres">
          <div
            v-for="(nodo, i) in buildRutaComponente(aspecto.id_componente).slice(0, -1)"
            :key="i"
            class="ctx-ruta-nodo"
          >
            <span v-if="nodo.tipo" class="ctx-ruta-tipo">{{ nodo.tipo }}</span>
            <span v-if="nodo.ordinal" class="ctx-ruta-ordinal">{{ nodo.ordinal }}</span>
            <span class="ctx-ruta-nombre">{{ nodo.nombre }}</span>
          </div>
        </div>
        <h3 class="aspecto-titulo">
          <span v-if="aspecto.id_componente && buildRutaComponente(aspecto.id_componente).at(-1)?.ordinal" class="ordinal">{{ buildRutaComponente(aspecto.id_componente!).at(-1)!.ordinal }}</span>
          {{ aspecto.aspecto }}
        </h3>

        <div v-for="ind in aspecto.indicadores" :key="ind.id_indicador" class="indicador">
          <div class="indicador-header">
            <strong>{{ ind.indicador }}</strong>
            <span class="promedio-chip">
              Promedio: {{ promedioIndicador(ind) ?? '—' }}
            </span>
          </div>

          <div v-for="p in ind.preguntas" :key="p.id_pregunta" class="pregunta-card">
            <p class="pregunta-enunciado">{{ p.enunciado }}</p>
            <p class="pregunta-meta">
              <span v-if="p.grupo_interes">{{ p.grupo_interes }} · </span>
              {{ p.respuestas.total }} respuesta(s)
              <span v-if="p.respuestas.promedio != null">
                · promedio de respuestas {{ Number(p.respuestas.promedio).toFixed(2) }}
              </span>
            </p>

            <!-- De qué formulario viene: un indicador puede tener preguntas
                 repartidas en varios, y sin esto no se sabe cuál se mira. -->
            <p v-if="p.formulario" class="pregunta-origen">
              <span class="origen-chip">{{ p.formulario.titulo || p.formulario.instrumento || 'Formulario' }}</span>
              <span v-if="p.formulario.general" class="origen-nota">pregunta general (va en todos los formularios)</span>
              <span v-else-if="p.formulario.grupo_interes" class="origen-nota">
                formulario de {{ p.formulario.grupo_interes }}
              </span>
            </p>

            <!-- Nota calculada desde la distribución con la tabla de
                 valoración. Es una sugerencia: la avala el calificador. -->
            <div v-if="p.nota_sugerida" class="nota-calculada">
              <template v-if="p.nota_sugerida.nota != null">
                <div class="nc-cab">
                  <span class="nc-valor">{{ p.nota_sugerida.nota.toFixed(2) }}</span>
                  <span class="nc-lbl">
                    Nota calculada ·
                    {{ p.nota_sugerida.desglose?.porcentaje_cumplimiento?.toFixed(2) }}% de cumplimiento
                  </span>
                  <button type="button" class="btn btn-sm guardar" @click="adoptar(p, p.nota_sugerida.nota)">
                    Usar esta nota
                  </button>
                </div>

                <details v-if="p.nota_sugerida.desglose" class="nc-desglose">
                  <summary>Ver cómo se calculó</summary>
                  <table class="nc-tabla">
                    <thead>
                      <tr><th>Opción</th><th>Respuestas</th><th>Puntos c/u</th><th>Subtotal</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="(c, i) in p.nota_sugerida.desglose.conteo" :key="i">
                        <td>{{ c.opcion }}</td>
                        <td>{{ c.cantidad }}</td>
                        <td>{{ c.puntos_por_respuesta }}</td>
                        <td>{{ c.subtotal.toFixed(2) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p class="nc-formula">
                    {{ p.nota_sugerida.desglose.puntaje_apreciado.toFixed(2) }} ÷
                    {{ p.nota_sugerida.desglose.puntaje_total.toFixed(2) }} × 100 =
                    <b>{{ p.nota_sugerida.desglose.porcentaje_cumplimiento.toFixed(2) }}%</b>
                    → × {{ p.nota_sugerida.desglose.escala_max }} ÷ 100 =
                    <b>{{ p.nota_sugerida.nota.toFixed(2) }}</b>
                  </p>
                  <p v-if="p.nota_sugerida.desglose.opciones_no_reconocidas.length" class="nc-aviso">
                    No se contaron (no están en la tabla de valoración):
                    {{ p.nota_sugerida.desglose.opciones_no_reconocidas.join(', ') }}
                  </p>
                </details>
              </template>

              <p v-else-if="p.nota_sugerida.motivo" class="nc-sin">
                {{ p.nota_sugerida.motivo }}
              </p>
            </div>

            <!-- Distribución por opción: es lo que se contrasta con la rúbrica -->
            <details v-if="p.respuestas.distribucion?.length" class="respuestas" open>
              <summary>Distribución de respuestas ({{ p.respuestas.total }} resp.)</summary>
              <ul class="lista-distribucion">
                <li
                  v-for="(d, i) in distribucionCompleta(p)"
                  :key="i"
                  :class="['fila-dist', { 'fila-dist--vacia': d.cantidad === 0 }]"
                >
                  <span class="dist-opcion" :title="d.opcion">{{ d.opcion }}</span>
                  <span class="dist-barra">
                    <span class="dist-relleno" :style="{ width: `${d.porcentaje}%` }"></span>
                  </span>
                  <span class="dist-pct">{{ d.porcentaje }}%</span>
                  <span class="dist-cant">{{ d.cantidad === 0 ? 'Sin respuestas' : `(${d.cantidad})` }}</span>
                </li>
              </ul>
            </details>

            <!-- Calificaciones previas reutilizables -->
            <div v-if="p.sugerencias.length" class="sugerencias">
              <p class="sugerencia-titulo">
                Ya se calificó esta pregunta para este aspecto:
              </p>
              <div v-for="s in p.sugerencias" :key="s.id" class="sugerencia">
                <span class="sug-nota">{{ Number(s.calificacion).toFixed(2) }}</span>
                <span class="sug-detalle">
                  {{ s.periodo ? `periodo ${s.periodo}` : 'sin periodo' }}
                  · {{ s.total_respuestas }} respuesta(s)
                  <template v-if="s.calificado_por"> · {{ s.calificado_por }}</template>
                </span>
                <button type="button" class="btn btn-sm guardar" @click="adoptar(p, s.calificacion)">
                  Usar esta nota
                </button>
              </div>
            </div>

            <div class="campo-nota">
              <label :for="`nota-${p.id_pregunta}`">Calificación ({{ escalaMax > 5 ? `1 – ${escalaMax}` : `0 – ${escalaMax}` }})</label>
              <input
                :id="`nota-${p.id_pregunta}`"
                v-model.number="notas[p.id_pregunta]"
                type="number"
                :min="escalaMax > 5 ? 1 : 0"
                :max="escalaMax"
                step="0.1"
                class="input-nota"
                :placeholder="escalaMax > 5 ? `1 - ${escalaMax}` : `0 - ${escalaMax}`"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="obs">Observaciones</label>
        <textarea id="obs" v-model="observaciones" rows="2" class="form-control"></textarea>
      </div>

      <div class="botones">
        <button class="btn salir" :disabled="guardando" @click="$router.back()">Cancelar</button>
        <button class="btn guardar" :disabled="guardando || !hayNotas" @click="guardar">
          {{ guardando ? 'Guardando…' : `Calificar ${totalNotas || ''}`.trim() }}
        </button>
      </div>
    </template>

    <RubricaVisorModal
      :visible="verRubrica"
      :criterio="rubrica"
      @cerrar="verRubrica = false"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Calificación de un instrumento de opinión.
 *
 * En encuesta y grupo focal la nota se deriva automáticamente de la distribución
 * de respuestas; en taller o mesa de trabajo la pone el calificador.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useAutenticar from '@/store/IngresoAutenticar'
import type { CriterioEvaluacion } from '@/interfaces/CriterioEvaluacion'
import type { AspectoCalificable, IndicadorCalificable, PreguntaCalificable, DistribucionRespuesta } from '@/services/CalificacionOpinionPreguntaService'
import {
  getVistaCalificacionService,
  calificarPreguntasService
} from '@/services/CalificacionOpinionPreguntaService'
import { getProcesoByIdService } from '@/services/ProcesoAutoevaluacionService'
import { obtenerConfiguracionService } from '@/services/ConfiguracionInstrumentoService'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import type Componente from '@/interfaces/Componente'
import RubricaVisorModal from '@/components/RubricaVisorModal.vue'
import RubricaTablaOpinion from '@/components/RubricaTablaOpinion.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

interface RutaNodo { tipo: string; ordinal: string; nombre: string }

const route = useRoute()
const router = useRouter()
const auth = useAutenticar()
const componentesStore = useComponentes()
const tiposStore = useGestionTipoComponente()

const idConfiguracion = Number(route.params.id_configuracion)
const idEjecucion = route.query.id_ejecucion ? Number(route.query.id_ejecucion) : null
const nombreVersion = ref('')

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length > 1 ? parts.slice(1).join('.') : ordinal
}

function buildRutaComponente(idComponente: number): RutaNodo[] {
  type NodoTemp = { idTipo: number | null | undefined; ordinal: string; nombre: string }
  const path: NodoTemp[] = []
  function buscar(nodos: Componente[], target: number, padres: NodoTemp[]): boolean {
    for (const n of nodos) {
      const nodoTemp: NodoTemp = { idTipo: n.id_tipo_componente, ordinal: formatOrdinal(n.ordinal ?? ''), nombre: n.nombre }
      if (n.id_componente === target) {
        padres.forEach(p => path.push(p))
        path.push(nodoTemp)
        return true
      }
      if (n.children?.length && buscar(n.children, target, [...padres, nodoTemp])) return true
    }
    return false
  }
  const raiz = componentesStore.componentes[0]
  if (raiz?.children) buscar(raiz.children, idComponente, [])
  return path.map(p => ({
    tipo: tiposStore.tipos.find(t => t.id_tipo_componente === p.idTipo)?.nombre ?? '',
    ordinal: p.ordinal,
    nombre: p.nombre
  }))
}

const escalaMax = computed<number>(() => {
  const raw = route.query.escala_max
  return Number(Array.isArray(raw) ? raw[0] : raw) || 5
})

const aspectos = ref<AspectoCalificable[]>([])
const esFormularioCompleto = ref(false)
const opcionesPorPregunta = ref<Record<number, string[]>>({})
const rubrica = ref<CriterioEvaluacion | null>(null)
const notas = ref<Record<number, number | null>>({})
const observaciones = ref('')
const cargando = ref(false)
const guardando = ref(false)
const error = ref('')
const verRubrica = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const notasValidas = computed(() =>
  Object.entries(notas.value)
    .filter(([, v]) => v !== null && v !== undefined && !isNaN(Number(v)))
    .map(([id, v]) => ({ id_pregunta: Number(id), calificacion: Number(v) }))
)

const hayNotas = computed(() => notasValidas.value.length > 0)
const totalNotas = computed(() => notasValidas.value.length)

/** Promedio en vivo del indicador: es lo que se guardará para él. */
function promedioIndicador(ind: IndicadorCalificable): string | null {
  const valores = ind.preguntas
    .map(p => notas.value[p.id_pregunta])
    .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
    .map(v => Number(v))

  if (!valores.length) return null
  return (valores.reduce((a, b) => a + b, 0) / valores.length).toFixed(2)
}

function adoptar(pregunta: PreguntaCalificable, calificacion: number) {
  notas.value[pregunta.id_pregunta] = Number(calificacion)
}

/**
 * Para preguntas de escala numérica (1..escalaMax) completa los valores que
 * nadie eligió, añadiéndolos con cantidad=0 y porcentaje=0.
 * Para opciones de texto devuelve la distribución tal como llegó del backend.
 */
function distribucionCompleta(p: PreguntaCalificable): DistribucionRespuesta[] {
  const dist = p.respuestas.distribucion
  const todasOpciones = opcionesPorPregunta.value[p.id_pregunta]

  // Si el snapshot tiene la lista completa de opciones, usarla como base
  if (todasOpciones?.length) {
    const existentes = new Map(dist.map(d => [d.opcion, d]))
    return todasOpciones.map(op => existentes.get(op) ?? { opcion: op, cantidad: 0, porcentaje: 0 })
  }

  // Fallback para escala numérica: completar 1..escalaMax con 0
  if (!dist.length) return dist
  const sonNumericos = dist.every(d => d.opcion.trim() !== '' && !isNaN(Number(d.opcion)))
  if (!sonNumericos) return dist
  const existentesNum = new Map(dist.map(d => [Number(d.opcion), d]))
  const resultado: DistribucionRespuesta[] = []
  for (let i = 1; i <= escalaMax.value; i++) {
    resultado.push(existentesNum.get(i) ?? { opcion: String(i), cantidad: 0, porcentaje: 0 })
  }
  return resultado
}

async function cargar() {
  cargando.value = true
  error.value = ''
  try {
    if (idEjecucion) {
      const proceso = await getProcesoByIdService(idEjecucion)
      nombreVersion.value = (proceso as any)?.version?.nombre ?? ''
      const idVersion = proceso?.id_version
      if (idVersion) {
        const [raiz] = await Promise.all([
          componentesStore.cargarRaizPorVersion(idVersion),
          tiposStore.getTipos()
        ])
        if (raiz?.id_componente) await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
      }
    }
    const [data, configResult] = await Promise.all([
      getVistaCalificacionService(idConfiguracion, idEjecucion),
      obtenerConfiguracionService(idConfiguracion)
    ])
    if (!data) {
      error.value = 'No se pudo cargar el instrumento.'
      return
    }
    // Construir mapa id_pregunta → opciones[] desde el snapshot de cada formulario
    if (configResult.ok && configResult.data) {
      const mapa: Record<number, string[]> = {}
      for (const formulario of configResult.data.formularios ?? []) {
        for (const snap of formulario.preguntas_snapshot ?? []) {
          if (snap.opciones?.length) mapa[snap.id_pregunta] = snap.opciones
        }
      }
      opcionesPorPregunta.value = mapa
    }
    esFormularioCompleto.value = data.modalidad === 'formulario_completo'
    aspectos.value = data.aspectos ?? []
    rubrica.value = data.rubrica ?? null
  } finally {
    cargando.value = false
  }
}

async function guardar() {
  if (!hayNotas.value) return

  const minVal = escalaMax.value > 5 ? 1 : 0
  const fueraDeRango = notasValidas.value.find(n => n.calificacion < minVal || n.calificacion > escalaMax.value)
  if (fueraDeRango) {
    mostrarMensaje(`Las calificaciones deben estar entre ${minVal} y ${escalaMax.value}.`, 'error')
    return
  }

  const idUsuario = auth.usuarioActual?.id
  if (!idUsuario) {
    mostrarMensaje('No se pudo identificar al usuario. Vuelva a iniciar sesión.', 'error')
    return
  }

  guardando.value = true
  try {
    const res = await calificarPreguntasService(idConfiguracion, {
      id_usuario: Number(idUsuario),
      id_ejecucion: idEjecucion,
      observaciones: observaciones.value || null,
      calificaciones: notasValidas.value
    })

    if (!res.ok) {
      mostrarMensaje(res.error ?? 'No se pudo calificar.', 'error')
      return
    }

    mensajeTexto.value = res.mensaje ?? 'Calificación registrada.'
    mensajeTipo.value = 'positivo'
    mensajeVisible.value = true
    setTimeout(() => router.back(), 1800)
  } finally {
    guardando.value = false
  }
}

onMounted(cargar)
</script>

<style scoped>
.calificar-opinion {
  background: #fff;
  padding: var(--space-lg, 1.5rem);
  border-radius: var(--radius-lg, 10px);
  margin: 20px;
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0, 0, 0, .08));
  font-family: var(--font-family, inherit);
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.header-acciones {
  display: flex;
  gap: 0.5rem;
}

h1 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
}

.ctx-modelo { display: inline-block; margin: 6px 0 0; padding: 3px 10px; background: #e8f0f8; border-left: 3px solid #0e69a0; border-radius: 3px; font-size: 0.85rem; font-weight: 700; color: #111; letter-spacing: 0.01em; }

.subtitulo {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: #666;
}

.divider {
  border-bottom: 1px solid #e6e6e6;
  margin: 1rem 0 1.25rem;
}

.rubrica-inline {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 1.5rem;
  background: #fafcff;
}

.rubrica-inline summary {
  cursor: pointer;
  font-weight: 600;
  font-size: 0.88rem;
  color: #1f4e79;
}

.aspecto {
  margin-bottom: 1.75rem;
}

.aspecto-titulo {
  font-size: 1rem;
  font-weight: 700;
  color: #1f4e79;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ordinal {
  background: #1f4e79;
  color: #fff;
  border-radius: 4px;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
}

.indicador {
  border-left: 3px solid #5b9bd5;
  padding-left: 0.9rem;
  margin-bottom: 1.25rem;
}

.indicador-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.6rem;
  font-size: 0.9rem;
}

.promedio-chip {
  background: #eef4fa;
  color: #0e69a0;
  border-radius: 12px;
  padding: 0.15rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.pregunta-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 0.8rem 0.9rem;
  margin-bottom: 0.7rem;
  background: #fdfdfd;
}

.ctx-padres {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}
.ctx-ruta-nodo { display: flex; align-items: center; gap: 0.5rem; }
.ctx-ruta-tipo { font-size: 0.68rem; font-weight: 700; color: #1f4e79; text-transform: uppercase; letter-spacing: 0.04em; }
.ctx-ruta-ordinal { background: #1f4e79; color: #fff; border-radius: 4px; padding: 0.1rem 0.45rem; font-size: 0.75rem; font-weight: 700; }
.ctx-ruta-nombre { font-size: 0.85rem; font-weight: 600; color: #1f4e79; }

.pregunta-enunciado {
  margin: 0 0 0.3rem;
  font-size: 0.88rem;
  font-weight: 600;
}

.pregunta-meta {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  color: #888;
}

/* Formulario del que viene la pregunta */
.pregunta-origen {
  margin: 0 0 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.origen-chip {
  background: #eef5fb;
  color: #0e69a0;
  border: 1px solid #c6ddf7;
  border-radius: 12px;
  padding: 0.1rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
}
.origen-nota { font-size: 0.72rem; color: #888; }

/* Nota calculada desde la distribución */
.nota-calculada {
  border: 1px solid #cfe6d4;
  background: #f4fbf6;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.7rem;
}
.nc-cab { display: flex; align-items: center; gap: 0.7rem; flex-wrap: wrap; }
.nc-valor { font-size: 1.35rem; font-weight: 800; color: #1d7a3a; line-height: 1; }
.nc-lbl { font-size: 0.78rem; color: #4a5b6b; flex: 1; }
.nc-desglose { margin-top: 0.5rem; }
.nc-desglose summary {
  cursor: pointer;
  font-size: 0.75rem;
  color: #0e69a0;
  font-weight: 600;
}
.nc-tabla { width: 100%; border-collapse: collapse; margin-top: 0.4rem; font-size: 0.75rem; }
.nc-tabla th, .nc-tabla td { text-align: left; padding: 0.2rem 0.4rem; border-bottom: 1px solid #e3efe6; }
.nc-tabla th { color: #4a5b6b; font-weight: 600; }
.nc-formula { margin: 0.5rem 0 0; font-size: 0.76rem; color: #4a5b6b; }
.nc-aviso { margin: 0.4rem 0 0; font-size: 0.74rem; color: #b06000; }
.nc-sin { margin: 0; font-size: 0.78rem; color: #888; font-style: italic; }

.respuestas summary {
  cursor: pointer;
  font-size: 0.78rem;
  color: #0e69a0;
}

.lista-distribucion {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.fila-dist {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.22rem 0;
  font-size: 0.78rem;
}

.dist-opcion {
  min-width: 130px;
  max-width: 200px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dist-barra {
  flex: 1;
  height: 12px;
  background: #eef2f6;
  border-radius: 6px;
  overflow: hidden;
  min-width: 60px;
}

.dist-relleno {
  display: block;
  height: 100%;
  background: #5b9bd5;
  border-radius: 6px;
}

.dist-pct {
  min-width: 46px;
  text-align: right;
  font-weight: 700;
  color: #0e69a0;
}

.dist-cant {
  color: #999;
  font-size: 0.72rem;
  min-width: 38px;
}

.fila-dist--vacia .dist-opcion { color: #bbb; }
.fila-dist--vacia .dist-relleno { background: #ddd; }
.fila-dist--vacia .dist-pct { color: #bbb; }
.fila-dist--vacia .dist-cant { color: #ccc; font-style: italic; }

.sugerencias {
  margin-top: 0.6rem;
  padding: 0.55rem 0.7rem;
  background: #fdf6e7;
  border-left: 3px solid #eea222;
  border-radius: 4px;
}

.sugerencia-titulo {
  margin: 0 0 0.4rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5c4a1e;
}

.sugerencia {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.78rem;
  padding: 0.2rem 0;
}

.sug-nota {
  font-weight: 700;
  color: #0e69a0;
}

.sug-detalle {
  flex: 1;
  color: #666;
}

.campo-nota {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.7rem;
}

.campo-nota label {
  font-size: 0.8rem;
  font-weight: 600;
}

.input-nota {
  width: 110px;
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 1rem 0;
}

.form-control {
  padding: 0.55rem 0.7rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.85rem;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn {
  padding: 8px 20px;
  height: 38px;
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-sm {
  height: auto;
  padding: 0.3rem 0.7rem;
  font-size: 0.75rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:not(:disabled):hover {
  background: #0e69a0;
  color: #fff;
}

.info {
  color: #666;
  font-size: 0.9rem;
}

.error {
  background: #f8d7da;
  color: #721c24;
  padding: 0.5rem 0.7rem;
  border-radius: 5px;
  font-size: 0.82rem;
}
</style>
